import type { ApiArea } from "../../shared/interfaces";
import type { OfflineAreasCapability, OfflineAreasSnapshot, Unsubscribe } from "../Platform";

const DATABASE = "primepoint-local-areas";
const STORE = "snapshots";
const LEASES = "leases";
const FRESH_FOR_MS = 5 * 60 * 1000;
const LEASE_FOR_MS = 30_000;
// Cross-tab completion normally wakes immediately. This bounded fallback also
// handles unavailable channels, dropped hints and an owner that died mid-fetch.
const WAIT_STEP_MS = 1_000;

type StoredSnapshot = { scope: string; areas: ApiArea[]; revision: number; fetchedAt: number };
type Lease = { scope: string; token: string; expiresAt: number };
type Flight = { followUp: boolean; promise: Promise<void> };

function validAreas(value: unknown): value is ApiArea[] {
    return (
        Array.isArray(value) &&
        new Set(
            value.map((area) =>
                typeof area === "object" && area !== null ? (area as ApiArea).id : null,
            ),
        ).size === value.length &&
        value.every(
            (area) =>
                typeof area === "object" &&
                area !== null &&
                typeof (area as ApiArea).id === "string" &&
                typeof (area as ApiArea).name === "string" &&
                typeof (area as ApiArea).is_common === "boolean" &&
                Number.isFinite((area as ApiArea).sheet_count),
        )
    );
}

function openDatabase(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DATABASE, 2);
        request.onupgradeneeded = () => {
            if (!request.result.objectStoreNames.contains(STORE))
                request.result.createObjectStore(STORE, { keyPath: "scope" });
            if (!request.result.objectStoreNames.contains(LEASES))
                request.result.createObjectStore(LEASES, { keyPath: "scope" });
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () =>
            reject(request.error ?? new Error("Unable to open local areas storage"));
    });
}

function requestValue<T>(request: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed"));
    });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
    return new Promise((resolve, reject) => {
        transaction.oncomplete = () => resolve();
        transaction.onerror = () =>
            reject(transaction.error ?? new Error("IndexedDB transaction failed"));
        transaction.onabort = () =>
            reject(transaction.error ?? new Error("IndexedDB transaction aborted"));
    });
}

/** Browser family owner. IndexedDB transactions, not BroadcastChannel, decide claims
 * and publication. A lease token prevents an expired request from deleting or replacing
 * a newer owner's work. */
export function createBrowserAreasCapability(options: {
    scope: () => string | null;
    fetchAreas: () => Promise<unknown>;
    now?: () => number;
}): OfflineAreasCapability {
    const now = options.now ?? Date.now;
    const listeners = new Set<(revision: number) => void>();
    const channel =
        typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel("primepoint-areas");
    const owner = globalThis.crypto?.randomUUID?.() ?? `${Math.random()}-${now()}`;
    const flights = new Map<string, Flight>();
    const waiters = new Set<(scope: string) => void>();
    let hintVersion = 0;

    const fresh = (snapshot: { fetchedAt: number }) => {
        const age = now() - snapshot.fetchedAt;
        return age >= 0 && age < FRESH_FOR_MS;
    };

    const waitForOwner = (scope: string, deadline: number, observedHint: number) => {
        // Do not lose a completion delivered while the claim transaction settled.
        if (hintVersion !== observedHint) return Promise.resolve();
        return new Promise<void>((resolve) => {
            const wake = (changedScope: string) => {
                if (changedScope !== scope) return;
                clearTimeout(timer);
                waiters.delete(wake);
                resolve();
            };
            const timer = setTimeout(
                () => wake(scope),
                Math.max(0, Math.min(WAIT_STEP_MS, deadline - now())),
            );
            waiters.add(wake);
        });
    };

    const readScope = async (scope: string): Promise<OfflineAreasSnapshot> => {
        const db = await openDatabase();
        try {
            const row = (await requestValue(
                db.transaction(STORE, "readonly").objectStore(STORE).get(scope),
            )) as StoredSnapshot | undefined;
            // Never return rows after the caller's authenticated scope changed mid-read.
            if (scope !== options.scope()) return { state: "missing" };
            return row
                ? {
                      state: "ready",
                      areas: row.areas,
                      revision: row.revision,
                      fetchedAt: row.fetchedAt,
                  }
                : { state: "missing" };
        } finally {
            db.close();
        }
    };

    channel?.addEventListener("message", (event: MessageEvent<unknown>) => {
        if (!event.data || typeof event.data !== "object") return;
        const { scope, revision } = event.data as { scope?: unknown; revision?: unknown };
        if (
            typeof scope !== "string" ||
            scope !== options.scope() ||
            (revision !== undefined &&
                (typeof revision !== "number" || !Number.isSafeInteger(revision) || revision < 0))
        )
            return;
        hintVersion += 1;
        waiters.forEach((wake) => wake(scope));
        // Scope-only hints signal completion/release without a semantic change.
        // A waiter always rechecks the durable lease and snapshot transaction.
        if (revision === undefined) return;
        // The channel carries only a wake-up hint. Read the committed IDB row before
        // notifying; no foreign payload is trusted or delivered.
        void readScope(scope)
            .then((snapshot) => {
                if (snapshot.state === "ready" && snapshot.revision >= revision) {
                    listeners.forEach((listener) => listener(snapshot.revision));
                }
            })
            .catch(() => undefined);
    });

    const claim = async (
        scope: string,
        force: boolean,
    ): Promise<{ token?: string; waitUntil?: number; fresh?: boolean }> => {
        const db = await openDatabase();
        try {
            // Another tab may publish between the optimistic read and this claim.
            // Check freshness under the same transaction as lease ownership so a
            // just-committed result does not trigger a redundant unresolved fetch.
            const transaction = db.transaction(force ? LEASES : [STORE, LEASES], "readwrite");
            const complete = transactionDone(transaction);
            if (!force) {
                const snapshot = (await requestValue(transaction.objectStore(STORE).get(scope))) as
                    StoredSnapshot | undefined;
                if (snapshot && fresh(snapshot)) {
                    await complete;
                    return { fresh: true };
                }
            }
            const leases = transaction.objectStore(LEASES);
            const current = (await requestValue(leases.get(scope))) as Lease | undefined;
            if (current && current.expiresAt > now()) {
                await complete;
                return { waitUntil: current.expiresAt };
            }
            const token = `${owner}:${globalThis.crypto?.randomUUID?.() ?? Math.random()}`;
            leases.put({ scope, token, expiresAt: now() + LEASE_FOR_MS } satisfies Lease);
            await complete;
            return { token };
        } finally {
            db.close();
        }
    };

    const release = async (scope: string, token: string): Promise<void> => {
        const db = await openDatabase();
        try {
            const transaction = db.transaction(LEASES, "readwrite");
            const complete = transactionDone(transaction);
            const leases = transaction.objectStore(LEASES);
            const current = (await requestValue(leases.get(scope))) as Lease | undefined;
            const released = current?.token === token;
            if (released) leases.delete(scope);
            await complete;
            if (released) channel?.postMessage({ scope });
        } finally {
            db.close();
        }
    };

    const refreshOne = async (scope: string, force: boolean): Promise<void> => {
        const before = await readScope(scope);
        if (!force && before.state === "ready" && fresh(before)) return;
        let token: string | undefined;
        while (scope === options.scope()) {
            const observedHint = hintVersion;
            const lease = await claim(scope, force);
            if (lease.fresh) return;
            if (lease.token) {
                token = lease.token;
                break;
            }
            await waitForOwner(scope, lease.waitUntil ?? now(), observedHint);
            // Neither a hint nor an old ready snapshot completes a refresh.
            // The next claim verifies freshness/expiry atomically. A forced
            // mutation always acquires its own lease after the foreign owner.
        }
        if (!token) return;
        try {
            if (scope !== options.scope()) return;
            const payload = await options.fetchAreas();
            if (!validAreas(payload)) throw new Error("Areas response is malformed");
            if (scope !== options.scope()) return;
            const db = await openDatabase();
            try {
                const transaction = db.transaction([STORE, LEASES], "readwrite");
                const complete = transactionDone(transaction);
                const store = transaction.objectStore(STORE);
                const leases = transaction.objectStore(LEASES);
                const currentLease = (await requestValue(leases.get(scope))) as Lease | undefined;
                if (currentLease?.token !== token || scope !== options.scope()) {
                    await complete;
                    return;
                }
                const current = (await requestValue(store.get(scope))) as
                    StoredSnapshot | undefined;
                const changed =
                    !current || JSON.stringify(current.areas) !== JSON.stringify(payload);
                const revision = changed ? (current?.revision ?? 0) + 1 : (current?.revision ?? 0);
                store.put({
                    scope,
                    areas: payload,
                    revision,
                    fetchedAt: now(),
                } satisfies StoredSnapshot);
                leases.delete(scope);
                await complete;
                if (changed) {
                    listeners.forEach((listener) => listener(revision));
                }
                channel?.postMessage(changed ? { scope, revision } : { scope });
            } finally {
                db.close();
            }
        } finally {
            // Conditional release cannot remove a newer owner's lease after expiry.
            await release(scope, token);
        }
    };

    return {
        scope: options.scope,
        read: async () => {
            const scope = options.scope();
            if (!scope) return { state: "missing" };
            if (typeof indexedDB === "undefined") throw new Error("IndexedDB is unavailable");
            return readScope(scope);
        },
        refresh: async ({ force = false, mutation = false } = {}) => {
            const scope = options.scope();
            if (!scope) throw new Error("Areas are unavailable until the signed-in scope is known");
            if (typeof indexedDB === "undefined") throw new Error("IndexedDB is unavailable");
            const active = flights.get(scope);
            if (active) {
                active.followUp ||= force || mutation;
                return active.promise;
            }
            const flight: Flight = { followUp: false, promise: Promise.resolve() };
            flight.promise = (async () => {
                let nextForce = force || mutation;
                do {
                    await refreshOne(scope, nextForce);
                    nextForce = flight.followUp;
                    flight.followUp = false;
                } while (nextForce && scope === options.scope());
            })().finally(() => flights.delete(scope));
            flights.set(scope, flight);
            return flight.promise;
        },
        subscribeCommitted(listener: (revision: number) => void): Unsubscribe {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
    };
}
